import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Snackbar,
} from "@mui/material";
import { School as SchoolIcon } from "@mui/icons-material";

const drawerWidth = 240;

const TimetablePage = () => {
  const [classValue, setClassValue] = useState("");
  const [sectionValue, setSectionValue] = useState("");
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [timetableData, setTimetableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const response = await fetch(
          "https://wsmsv1.aidigitalsec.com/api/Teacher/GetClass",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: sessionStorage.getItem("token"),
            },
            body: JSON.stringify({
              teacherId: sessionStorage.getItem("employeeId"),
            }),
          }
        );
        const data = await response.json();
        if (!data || data.data === null || data.msg === "Record Not Found") {
          setClasses([]);
        } else {
          setClasses(data);
        }
      } catch (err) {
        console.error("Error fetching classes:", err);
        setClasses([]);
      }
    };

    fetchClasses();
  }, []);

  const fetchSections = async (classId) => {
    try {
      const response = await fetch(
        "https://wsmsv1.aidigitalsec.com/api/Teacher/ddlSection_clsId",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: sessionStorage.getItem("token"),
          },
          body: JSON.stringify({ classId }),
        }
      );

      const data = await response.json();
      if (!data || data.data === null) {
        setSections([]);
      } else {
        setSections(data);
      }
    } catch (err) {
      console.error("Fetch sections error:", err);
      setSections([]);
    }
  };

  const fetchTimetable = async () => {
    if (!classValue || !sectionValue) {
      setSnackbar({
        open: true,
        message: "Please select both class and section.",
        severity: "warning",
      });
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        "https://wsmsv1.aidigitalsec.com/api/TimeTable/GetTimeTable",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: sessionStorage.getItem("token"),
          },
          body: JSON.stringify({
            classId: classValue,
            sectionId: sectionValue,
          }),
        }
      );

      const data = await response.json();
      if (!Array.isArray(data) || data.length === 0) {
        setTimetableData([]);
        setError("No timetable data available.");
      } else {
        setTimetableData(processTimeTableData(data));
      }
    } catch (err) {
      setError(err.message || "Failed to fetch timetable data");
    } finally {
      setLoading(false);
    }
  };

  const processTimeTableData = (data) => {
    if (!data || !Array.isArray(data)) return [];

    const periods = {};
    data.forEach((item) => {
      const key = `${item.startTime}-${item.endTime}-${item.periodSequenceNo}`;
      if (!periods[key]) {
        periods[key] = {
          startTime: item.startTime,
          endTime: item.endTime,
          period: `Period ${item.periodSequenceNo}`,
          periodSequenceNo: item.periodSequenceNo,
          monday: "-",
          tuesday: "-",
          wednesday: "-",
          thursday: "-",
          friday: "-",
          saturday: "-",
        };
      }

      const dayMap = {
        2: "monday",
        3: "tuesday",
        4: "wednesday",
        5: "thursday",
        6: "friday",
        7: "saturday",
      };
      if (dayMap[item.dayofWeekId]) {
        let content = `${item.subjectName} ~ ${item.teacherName}`;
        if (item.roomNo && item.roomNo !== 0) content += `\nRoom ${item.roomNo}`;
        periods[key][dayMap[item.dayofWeekId]] = content;
      }
    });

    return Object.values(periods).sort((a, b) => a.periodSequenceNo - b.periodSequenceNo);
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    if (timeStr.includes("AM") || timeStr.includes("PM")) return timeStr;

    const [hours, minutes] = timeStr.split(":").map(Number);
    const period = hours >= 12 ? "PM" : "AM";
    const displayHours = hours > 12 ? hours - 12 : hours || 12;
    return `${displayHours}:${minutes.toString().padStart(2, "0")} ${period}`;
  };

  const handleClassChange = (event) => {
    const selectedClassId = event.target.value;
    setClassValue(selectedClassId);
    setSectionValue("");
    fetchSections(selectedClassId);
  };

  const handleSectionChange = (event) => {
    setSectionValue(event.target.value);
  };

  const handleSnackbarClose = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <Box component="main" sx={{
      width: { md: `calc(100% - ${drawerWidth}px)` },
      ml: { md: `${drawerWidth}px` },
      mt: { xs: 0, sm: '64px' },
      p: { xs: 1, md: 3 },
      minHeight: "100vh",
    }}>
      <Card
  sx={{
    maxWidth: "100%",
    mx: "auto",
    borderRadius: 2,
    boxShadow: "0 10px 30px #063552",
  }}
>

      <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <SchoolIcon sx={{ fontSize: 40, color: "#063552", mr: 1 }} />
<Typography
  variant="h4"
  sx={{
    fontWeight: "bold",
    color: "#063552", // darker amber text
  }}
>
  Time Table
</Typography>

          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
            <FormControl fullWidth>
              <InputLabel id="class-select-label">Class</InputLabel>
              <Select
                labelId="class-select-label"
                value={classValue}
                onChange={handleClassChange}
                label="Class"
              >
                {classes.length > 0 ? (
                  classes.map((cls) => (
                    <MenuItem key={cls.classId} value={cls.classId}>
                      {cls.className}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>No classes available</MenuItem>
                )}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="section-select-label">Section</InputLabel>
              <Select
                labelId="section-select-label"
                value={sectionValue}
                onChange={handleSectionChange}
                disabled={!classValue}
                label="Section"
              >
                {sections.length > 0 ? (
                  sections.map((section) => (
                    <MenuItem key={section.sectionId} value={section.sectionId}>
                      {section.sectionName}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>No sections available</MenuItem>
                )}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
           <Button
  variant="contained"
  onClick={fetchTimetable}
  disabled={loading}
  sx={{
    background: "linear-gradient(135deg, #080F1F 0%, #102A43 55%, #1B4965 100%)",
    color: "#fff",
    fontWeight: 600,
    px: 3,
    "&:hover": {
      background: "linear-gradient(135deg, #080F1F 0%, #102A43 55%, #1B4965 100%)",
    },
  }}
>

              {loading ? <CircularProgress size={24} /> : "Show Timetable"}
            </Button>
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress size={40} />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          ) : (
            <TableContainer component={Paper} sx={{ boxShadow: 0, border: "1px solid #e0e0e0" }}>
              <Table sx={{ minWidth: 650 }} aria-label="timetable">
                <TableHead>
                  <TableRow sx={{ background: "linear-gradient(135deg, #080F1F 0%, #102A43 55%, #1B4965 100%)"}}>
                    <TableCell sx={{ color: "white", fontWeight: 600, p: 2 }}>Start Time</TableCell>
                    <TableCell sx={{ color: "white", fontWeight: 600, p: 2 }}>End Time</TableCell>
                    <TableCell sx={{ color: "white", fontWeight: 600, p: 2 }}>Period</TableCell>
                    <TableCell sx={{ color: "white", fontWeight: 600, p: 2 }}>Monday</TableCell>
                    <TableCell sx={{ color: "white", fontWeight: 600, p: 2 }}>Tuesday</TableCell>
                    <TableCell sx={{ color: "white", fontWeight: 600, p: 2 }}>Wednesday</TableCell>
                    <TableCell sx={{ color: "white", fontWeight: 600, p: 2 }}>Thursday</TableCell>
                    <TableCell sx={{ color: "white", fontWeight: 600, p: 2 }}>Friday</TableCell>
                    <TableCell sx={{ color: "white", fontWeight: 600, p: 2 }}>Saturday</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {timetableData.length > 0 ? (
                    timetableData.map((row, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          "&:nth-of-type(odd)": { bgcolor: "#f9f9f9" },
                          "&:hover": {
  bgcolor: "rgba(245,158,11,0.12)", // soft amber hover
},
                      }}
                      >
                        <TableCell sx={{ p: 2, textAlign: "center" }}>{formatTime(row.startTime)}</TableCell>
                        <TableCell sx={{ p: 2, textAlign: "center" }}>{formatTime(row.endTime)}</TableCell>
                        <TableCell sx={{ p: 2, textAlign: "center" }}>{row.period}</TableCell>
                        <TableCell sx={{ p: 2, whiteSpace: "pre-line" }}>{row.monday || "-"}</TableCell>
                        <TableCell sx={{ p: 2, whiteSpace: "pre-line" }}>{row.tuesday || "-"}</TableCell>
                        <TableCell sx={{ p: 2, whiteSpace: "pre-line" }}>{row.wednesday || "-"}</TableCell>
                        <TableCell sx={{ p: 2, whiteSpace: "pre-line" }}>{row.thursday || "-"}</TableCell>
                        <TableCell sx={{ p: 2, whiteSpace: "pre-line" }}>{row.friday || "-"}</TableCell>
                        <TableCell sx={{ p: 2, whiteSpace: "pre-line" }}>{row.saturday || "-"}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={9} sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>
                        No timetable data available.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TimetablePage;